import {
  FormCheckbox,
  FormDatePicker,
  FormInput,
  FormSelect,
  FormTextarea,
} from "@/components";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import { X } from "lucide-react";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { UpdatePdcProps } from "../types";
import {
  IS_PAYMENT_RECEIVED,
  PAID_AMOUNT,
  PAID_DATE,
  PAYMENT_RECEIVED_ON,
  PAYMENT_REMARKS,
  PAYMENT_STATUS,
  paymentStatusOptions,
} from "./consts";
import useUpdatePayment from "./useUpdatePayment";

const AddEditPayment: FC<UpdatePdcProps> = ({ onCancel, open, editData }) => {
  const { form, onSubmit, requiredFields, isMobile } = useUpdatePayment({
    onCancel,
    editData,
  });

  const { errors } = form.formState;

  return (
    <Drawer open={open} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">
              {editData ? "Update" : "Add"} Payment
            </DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="relative bg-white rounded-lg pt-[30px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[130px] px-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[25px]">
                <FormInput
                  name={PAID_AMOUNT}
                  control={form.control}
                  label="Paid Amount"
                  placeholder="Please enter"
                  error={errors[PAID_AMOUNT]?.message}
                  isRequired={requiredFields.includes(PAID_AMOUNT)}
                  variant="float"
                  disabled={editData ? true : false}
                />
                <FormDatePicker
                  name={PAID_DATE}
                  control={form.control}
                  label="Paid Date"
                  placeholder="Autopopulated"
                  error={errors[PAID_DATE]?.message}
                  isRequired={requiredFields.includes(PAID_DATE)}
                  disabled={editData ? true : false}
                />
                <FormSelect
                  name={PAYMENT_STATUS}
                  control={form.control}
                  label="Payment Status"
                  placeholder="Please select"
                  error={errors[PAYMENT_STATUS]?.message}
                  isRequired={requiredFields.includes(PAYMENT_STATUS)}
                  options={paymentStatusOptions}
                  disabled={editData ? true : false}
                />
                <FormCheckbox
                  name={IS_PAYMENT_RECEIVED}
                  control={form.control}
                  label="Payment Received by Cognospace"
                  error={errors[IS_PAYMENT_RECEIVED]?.message}
                  isRequired={requiredFields.includes(IS_PAYMENT_RECEIVED)}
                />
                <FormDatePicker
                  name={PAYMENT_RECEIVED_ON}
                  control={form.control}
                  label="Payment Received On"
                  placeholder="Please select"
                  error={errors[PAYMENT_RECEIVED_ON]?.message}
                  isRequired={requiredFields.includes(PAYMENT_RECEIVED_ON)}
                />
                <div className="md:col-span-2">
                  <FormTextarea
                    name={PAYMENT_REMARKS}
                    control={form.control}
                    label="Payment Remarks"
                    placeholder="Please enter"
                    error={errors[PAYMENT_REMARKS]?.message}
                    isRequired={requiredFields.includes(PAYMENT_REMARKS)}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-3 absolute bottom-3 w-full bg-white justify-end pb-[25px] z-[3]">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit">{editData ? "Update" : "Submit"}</Button>
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default AddEditPayment;
